/**
 * Identifiers (engine.md §1). The ONLY place ids are created.
 *
 *   H(parts…) = Crockford base32 (lower case) of SHA-256 over the parts joined with '\n' (UTF-8).
 *   ulid()    = 26 chars: 48-bit millisecond time (10 chars) + 80 random bits (16 chars).
 *
 * Every id matches /^[a-z0-9_]+$/.
 */

import { sha256 } from '@noble/hashes/sha2.js';
import { bytesToHex, utf8ToBytes } from '@noble/hashes/utils.js';
import type {
  ConnectorId, DeviceId, DigestId, EpochMs, EventId, FolderId, MessageId, RuleId, SourceId, TagId, TopicId, ViewId,
} from '../contracts/ids.js';
import type { ActorKind } from '../contracts/entities.js';
import type { EventKind } from '../contracts/entities.js';
import type { Clock, RandomSource } from '../contracts/platform.js';
import type { SourceKind } from '../contracts/sources.js';

export const BASE32_ALPHABET = '0123456789abcdefghjkmnpqrstvwxyz';

/** Bit-stream base32 (most significant bit first); the last group is zero-padded on the right. */
export function base32(bytes: Uint8Array): string {
  let out = '';
  let buffer = 0;
  let bits = 0;
  for (const byte of bytes) {
    buffer = (buffer << 8) | byte;
    bits += 8;
    while (bits >= 5) {
      out += BASE32_ALPHABET[(buffer >>> (bits - 5)) & 31];
      bits -= 5;
    }
    buffer &= (1 << bits) - 1;
  }
  if (bits > 0) out += BASE32_ALPHABET[(buffer << (5 - bits)) & 31];
  return out;
}

/** H(parts…): base32 of SHA-256 over the parts joined with '\n'. 52 characters. */
export function hashParts(...parts: string[]): string {
  return base32(sha256(utf8ToBytes(parts.join('\n'))));
}

/** SHA-256 hex of a UTF-8 string. */
export function sha256Hex(s: string): string {
  return bytesToHex(sha256(utf8ToBytes(s)));
}

/** Maximum externalId length kept verbatim; longer ids are replaced by their SHA-256 hex. */
export const EXTERNAL_ID_MAX = 512;

export function normaliseExternalId(externalId: string): string {
  return externalId.length > EXTERNAL_ID_MAX ? sha256Hex(externalId) : externalId;
}

export function sourceId(kind: SourceKind, locator: string): SourceId {
  return ('s_' + hashParts('source', kind, locator).slice(0, 16)) as SourceId;
}

export function messageId(source: SourceId, externalId: string): MessageId {
  return ('m_' + hashParts('message', source, normaliseExternalId(externalId)).slice(0, 24)) as MessageId;
}

/**
 * The deterministic tag id for a folded name. The caller (tags.create) must fall back to
 * `newTagId()` when this id belongs to a live tag whose folded name now differs (it was renamed).
 */
export function tagIdForName(nameFold: string): TagId {
  return ('t_' + hashParts('tag', nameFold).slice(0, 16)) as TagId;
}

/** Deterministic id of an AUTOMATIC event: the same step on two devices is one row. */
export function automaticEventId(parts: {
  messageId: MessageId;
  kind: EventKind;
  actorKind: ActorKind;
  actorId: string | null;
  fromFolderId: FolderId | null;
  toFolderId: FolderId | null;
}): EventId {
  const h = hashParts(
    'event', parts.messageId, parts.kind, parts.actorKind, parts.actorId ?? '', parts.fromFolderId ?? '', parts.toFolderId ?? '',
  );
  return ('e_' + h.slice(0, 24)) as EventId;
}

/** 26-character ULID in lower-case Crockford base32. */
export function ulid(now: EpochMs | number, random: RandomSource): string {
  let time = Math.max(0, Math.floor(now));
  let t = '';
  for (let i = 0; i < 10; i++) {
    t = BASE32_ALPHABET[time % 32] + t;
    time = Math.floor(time / 32);
  }
  // 80 random bits = exactly 16 base32 characters.
  return t + base32(random.bytes(10));
}

export type IdSource = { clock: Clock; random: RandomSource };

const make = <T>(prefix: string) => (src: IdSource): T => (prefix + ulid(src.clock.now(), src.random)) as T;

export const newFolderId = make<FolderId>('f_');
export const newTopicId = make<TopicId>('tp_');
export const newRuleId = make<RuleId>('r_');
export const newViewId = make<ViewId>('v_');
export const newDigestId = make<DigestId>('d_');
export const newConnectorId = make<ConnectorId>('c_');
export const newTagId = make<TagId>('t_');
/** Manual events (people, assistants). Automatic events use automaticEventId. */
export const newEventId = make<EventId>('e_');

/** 'dv_' + 8 base32 characters (40 random bits). */
export function newDeviceId(random: RandomSource): DeviceId {
  return ('dv_' + base32(random.bytes(5))) as DeviceId;
}

/** An opaque id for undo entries and similar in-memory handles. */
export function newOpaqueId(prefix: string, src: IdSource): string {
  return prefix + ulid(src.clock.now(), src.random);
}

export const ID_PATTERN = /^[a-z0-9_]+$/;
export function isValidId(value: unknown): value is string {
  return typeof value === 'string' && value.length > 0 && value.length <= 128 && ID_PATTERN.test(value);
}
